import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CreditCard,
  Headphones,
  MapPin,
  Settings,
  ShieldCheck,
  ShoppingBag,
  Wrench
} from 'lucide-react';
import { Button } from '../ui/button';
import { ZWHEEL_EXPERIENCE_DATA } from '../../data/services';

const iconMap: Record<string, React.ReactNode> = {
  ShieldCheck: <ShieldCheck className="w-6 h-6" />,
  Wrench: <Wrench className="w-6 h-6" />,
  Headphones: <Headphones className="w-6 h-6" />,
  Settings: <Settings className="w-6 h-6" />,
  ShoppingBag: <ShoppingBag className="w-6 h-6" />,
  CreditCard: <CreditCard className="w-6 h-6" />,
  MapPin: <MapPin className="w-6 h-6" />
};

export function ZwheelExperience() {
  const features = Object.entries(ZWHEEL_EXPERIENCE_DATA)
    .filter(([key]) => key !== 'techCenters')
    .map(([, value]) => value);

  return (
    <section className="py-28 bg-[linear-gradient(180deg,#0e0e0e_0%,#171717_72%,#18392b_100%)] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 h-64 w-64 rounded-full bg-white/5 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-[#f1ddbf]/10 blur-3xl" />
      </div>

      <div className="home-shell">
        <div className="grid grid-cols-1 xl:grid-cols-[0.92fr_1.08fr] gap-8 items-start mb-10">
          <div className="max-w-2xl">
            <motion.span
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-sm font-bold tracking-[0.18em] uppercase text-white/60 block mb-4"
            >
              La experiencia Zwheel
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-5"
            >
              Un ecosistema que no se queda solo en el patinete
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg md:text-xl text-white/70 leading-relaxed"
            >
              Garantia, reparacion, soporte, recambios originales y financiacion. Esta seccion debe sentirse como un sistema de servicio alrededor del producto.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.14 }}
            className="rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-sm"
          >
            <div className="flex items-center gap-3 mb-4 text-[#f4d7a2]">
              <MapPin className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase tracking-[0.18em]">Asistencia oficial</span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Red de soporte en toda España</h3>
            <p className="text-white/68 leading-relaxed mb-5">
              Compra guiada, mantenimiento, reparacion y ayuda para identificar el repuesto correcto antes de entrar en la tienda.
            </p>
            <Button asChild className="bg-white text-black hover:bg-[#f4ede2]">
              <Link to="/soporte">
                Ir a soporte
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.article
              key={feature.id}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: index * 0.06 }}
              className="rounded-[1.85rem] border border-white/10 bg-white/6 p-7 text-white backdrop-blur-sm shadow-[0_18px_70px_rgba(0,0,0,0.12)]"
            >
              <div className="inline-flex items-center justify-center rounded-2xl bg-white text-black p-4 mb-6">
                {iconMap[feature.icon]}
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 mb-3">Servicio oficial</div>
              <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
              <p className="text-white/70 leading-relaxed">{feature.description}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
